import React, { useEffect, useState } from "react";
import { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { getUniCourtCaseSearch } from "../../redux/actions/order.action";
import CaseResultTable from "../../components/order/CaseListTable";
// import { getOrderDraft, placeOrder, placeOrderDraft, deleteOrderDraft, getUniCourtCaseData } from "../../redux/actions/order.action";

interface Filter {
  id: number;
  field: string;
  operator: string;
  value: string;
  scope: string;
  logic: "AND" | "OR" | "NOT";
}

const defaultFilter = (id: number): Filter => ({
  id,
  field: "allFields",
  operator: "contains",
  value: "",
  scope: "any",
  logic: "AND",
});

export default function SearchOrderPage() {
  const dispatch: Dispatch = useDispatch();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filter[]>([defaultFilter(1)]);
  const [caseList, setCaseList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isChangedField, setIsChangedField] = useState(false);
  const { draft } = useSelector(({ orderReducer }) => orderReducer);

  if (draft) {
    navigate("/order/save");
  }
  const handleChange = (id: number, key: keyof Filter, value: any) => {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f))
    );
    if (key === "field") setIsChangedField(true);
  };

  const addFilter = () => {
    const newId = filters.length ? filters[filters.length - 1].id + 1 : 1;
    setFilters([...filters, defaultFilter(newId)]);
  };

  const removeFilter = (id: number) => {
    setFilters(filters.filter((f) => f.id !== id));
  };

  const handleSubmit = () => {
    const queryString = buildQueryString(filters);
    console.log("Query String:", queryString);
    if (queryString.length === 0) {
      setCaseList([]);
      return;
    }
    getUniCourtDataList(queryString);
  };
  const handleCancel = () => {
    setCaseList([]);
    setFilters([defaultFilter(1)]);
  };

  const getUniCourtDataList = ((queryString: string) => {
    setIsLoading(true);
    dispatch(getUniCourtCaseSearch(queryString))
      .then((response: any) => {
        setCaseList(response?.list || []);
        setTotalCount(response?.totalCount || 0);
        setIsLoading(false);
      })
      .catch((error: Error) => {
        console.error(error);
        setIsLoading(false);
      });
  });

  useEffect(() => {
    filters.map((f) => {
      if (f.field === "caseNumber" || f.field === "caseId" || f.field === "Attorney.barNumber") {
        handleChange(f.id, "operator", "exact");
      } else {
        handleChange(f.id, "operator", "contains");
      }
    });
    setIsChangedField(false);
  }, [isChangedField]);

  const buildQueryString = (filters: Filter[]) => {
    if (!filters || filters.length === 0) return "";

    const queryParts: string[] = [];
    let firstClause = "";

    for (let i = 0; i < filters.length; i++) {
      const { field, operator, value, logic } = filters[i];
      if (!value) continue;

      if (field === "allFields") {
        firstClause = value; // No formatting, just plain text
        continue;
      }

      let query = "";

      if (field.includes(".")) {
        const [group, subField] = field.split(".");
        const capitalizedGroup = capitalize(group);
        const inner =
          operator === "contains"
            ? `${subField}:${value}`
            : operator === "startsWith"
              ? `${subField}:${value}*`
              : `${subField}:"${value}"`;

        query = `(${capitalizedGroup}:(${inner}))`;
      } else {
        query =
          operator === "contains"
            ? `${field}:${value}`
            : operator === "startsWith"
              ? `${field}:${value}*`
              : `${field}:"${value}"`;
      }

      const prefix = queryParts.length > 0 || firstClause ? `${logic} ` : "";
      queryParts.push(`${prefix}${query}`);
    }

    return [firstClause, ...queryParts].filter(Boolean).join(" ");
  };

  function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <Paper elevation={3} sx={{ padding: 4, margin: "auto", mt: 4 }} className="container w-full">
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Create the query for your case search
          <p className="text-xs color-gray mt-1 text-[#388e3c]">We suggest you use the full case number to find your case.</p>
        </Typography>

        {filters.map((filter) => (
          <Paper key={filter.id} sx={{ p: 2, mb: 2 }} elevation={2} className="shadow-none bg-transparent">
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  value={filter.field}
                  label="Field"
                  onChange={(e) => handleChange(filter.id, "field", e.target.value)}
                >
                  <MenuItem value="allFields">All Fields</MenuItem>
                  <MenuItem value="caseNumber">Case Number</MenuItem>
                  <MenuItem value="caseName">Case Name</MenuItem>
                  <MenuItem value="caseId">Case Id</MenuItem>
                  <MenuItem value="party.name">Party Name</MenuItem>
                  <MenuItem value="Attorney.name">Attorney Name</MenuItem>
                  <MenuItem value="Attorney.barNumber">Attorney BarNumber</MenuItem>
                  <MenuItem value="Court.name">Court Name</MenuItem>
                  <MenuItem value="CourtLocation.name">Court Location Name</MenuItem>
                  <MenuItem value="CourtLocation.city">Court Location City</MenuItem>
                  {/* Add more fields as needed */}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={filter.operator}
                  label="Operator"
                  onChange={(e) => handleChange(filter.id, "operator", e.target.value)}
                >
                  {
                    filter.field !== "caseNumber" &&
                    filter.field !== "caseId" &&
                    filter.field !== "Attorney.barNumber"
                    && <MenuItem value="contains">Contains</MenuItem>
                  }
                  {filter.field !== "allFields" &&
                    filter.field !== "caseName" &&
                    // filter.field !== "Court.name" &&
                    filter.field !== "CourtLocation.city" &&
                    filter.field !== "CourtLocation.name" &&
                    <MenuItem value="exact">Exact</MenuItem>
                  }
                </Select>
              </FormControl>

              <TextField
                label="Value"
                value={filter.value}
                onChange={(e) => handleChange(filter.id, "value", e.target.value)}
              />
              <IconButton onClick={() => removeFilter(filter.id)}>
                <DeleteIcon color="error" />
              </IconButton>
            </Stack>
            <Stack>
              <p className="text-xs color-gray mt-1">For Operator: Select <strong>Contains</strong> for a single-word search (e.g., <em>&quot;Charlotte&quot;</em>), and <strong>Exact</strong> for a phrase with two or more words (e.g., <em>&quot;Charlotte General&quot;</em>).</p>
            </Stack>
          </Paper>
        ))}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <div>
            <Button variant="contained" color="primary" onClick={addFilter}>
              Add Filter
            </Button>
            {totalCount > 10 ? <p style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>There are more than 10 cases that match the criteria. Please add a filter.</p> : ""}
          </div>
          <Box>
            <Button variant="outlined" color="error" sx={{ mr: 2 }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={handleSubmit}>
              Search
            </Button>
          </Box>
        </Box>
      </Box>
      <CaseResultTable caseList={caseList} isLoading={isLoading} />

    </Paper>

  );
}